import { Box, Tooltip, Typography, useTheme } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { isEnabled } from "lib/@components/feature"
import { parentFind } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { uniqueId } from "library/database/split-id.mjs"
import { getShareLinkCommentSummary } from "routes/schedule/plugins/share-link-comments/share-link-comments.controller"
import { getShareLinkCommentRegimeId } from "./share-link-comment-regime-id"
import { FEATURE_FLAG_SHARE_LINK_COMMENTS } from "./share-link-comments-feature-flag"

// Anchor types that count as "schedule comments" for the inline indicator. `sharelink` and `folder`
// comments share a sentinel scheduleId but are NOT about the schedule itself, so they are excluded.
const SCHEDULE_ANCHOR_TYPES = new Set(["schedule", "intro", "task"])
const EMPTY_GROUPS = []

export function ScheduleCommentIndicator({
    schedule: scheduleProp,
    schedulePath: schedulePathProp,
    parentItem: parentItemProp,
    facility: facilityProp,
    facilityGroup: facilityGroupProp,
} = {}) {
    const theme = useTheme()
    const setSearchParams = useSearchParamsBatch()
    const context = useBoundContext()
    const schedule = scheduleProp ?? context.schedule ?? context.target
    const schedulePath = schedulePathProp ?? context.schedulePath
    const parentItem = parentItemProp ?? context.parentItem
    const facility = facilityProp ?? context.facility
    const facilityGroup = facilityGroupProp ?? context.facilityGroup
    const scheduleId = schedulePath ?? schedule?.id ?? schedule?._id
    const featureOn = isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)
    const itemId = parentItem?.id ?? facility?.id ?? facilityGroup?.id ?? scheduleId
    const rootItem = parentFind(parentItem ?? facility ?? facilityGroup, isPlanRoot)
    const regimeId = featureOn && scheduleId ? getShareLinkCommentRegimeId({ itemId, rootItem }) : undefined
    const groups = getShareLinkCommentSummary.useResults(regimeId ? { regimeId } : undefined) ?? EMPTY_GROUPS
    const folderId = getRowFolderId(parentItem ?? facility ?? facilityGroup)
    const count = regimeId ? countScheduleComments(groups, scheduleId, { folderId }) : 0

    return (
        <If value={count > 0}>
            <Tooltip arrow title={getTooltipTitle()}>
                <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={openComments}
                    data-cy={`schedule-comment-indicator-${scheduleId}`}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        ml: 1,
                        px: 0.5,
                        cursor: "pointer",
                        borderRadius: theme.shape.borderRadius / 8,
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                    }}
                >
                    <Iconify icon="mdi:comment-text-outline" width={14} height={14} />
                    <Typography variant="caption" component="span" sx={{ lineHeight: 1 }}>
                        {count}
                    </Typography>
                </Box>
            </Tooltip>
        </If>
    )

    function openComments(event) {
        event.stopPropagation()
        setSearchParams({ statsTab: "Comments", commentTarget: scheduleId })
    }

    function getTooltipTitle() {
        return count === 1 ? "1 comment" : `${count} comments`
    }
}

// Exported for direct unit testing. Sum, across every share-link group, the root-thread summary
// entries that are anchored to THIS schedule. Schedule identity is matched via `uniqueId` so the
// table-source suffix on the id (e.g. `schedule` vs `schedule-inconsistency`) does not matter.
// `sharelink`/`folder` anchors are excluded — they are not schedule comments.
export function countScheduleComments(groups, scheduleId, { folderId } = {}) {
    if (!scheduleId) return 0
    const targetScheduleId = getBareScheduleId(scheduleId)
    const targetCandidates = getScheduleIdentityCandidates(scheduleId)
    let count = 0
    for (const group of groups ?? []) {
        for (const comment of group?.comments ?? []) {
            if (!SCHEDULE_ANCHOR_TYPES.has(comment?.anchorType)) continue
            if (!matchesComment(comment)) continue
            count++
        }
    }
    return count

    // Canonical identity first: same bare schedule in the same RESOLVED containing folder. A
    // comment's schedulePath records the share link's grouping (often facility-level), not the
    // regime tree's actual folder, so when the summary has resolved the real folder
    // (scheduleFolderId) and the row knows its own folder, those are the ids to compare. Without
    // both folder ids we keep the legacy path/id identity matching.
    function matchesComment(comment) {
        if (folderId && comment?.scheduleFolderId) {
            if (getBareScheduleId(comment.scheduleId ?? comment.schedulePath) !== targetScheduleId) return false
            return uniqueId(comment.scheduleFolderId) === uniqueId(folderId)
        }
        return hasSharedScheduleIdentity(targetCandidates, comment?.scheduleId, comment?.schedulePath)
    }
}

// The schedule's own id with all grouping stripped: last path segment, no version dot-suffix, no
// table-source suffix.
function getBareScheduleId(value) {
    if (!value) return undefined
    const lastSegment = `${value}`.split("!").at(-1)
    return uniqueId(`${lastSegment}`.split(".").at(0))
}

// The row's containing folder, from its bound parent tree item. The id's last path segment is the
// node id in both draft ("U26i…") and published ("~live|tree!U26i…") views. The plan root is not a
// folder, so root-level rows fall back to legacy identity matching.
function getRowFolderId(item) {
    if (!item || item[PlanRoot]) return undefined
    const id = item.id ?? item._id
    if (!id) return undefined
    return `${id}`.split("!").at(-1)
}

function hasSharedScheduleIdentity(targetCandidates, ...values) {
    const commentCandidates = getScheduleIdentityCandidates(...values)
    return [...targetCandidates].some((candidate) => commentCandidates.has(candidate))
}

function getScheduleIdentityCandidates(...values) {
    const candidates = new Set()
    for (const value of values) {
        if (!value) continue
        const stringValue = `${value}`
        if (isSchedulePath(stringValue)) {
            candidates.add(normalizeSchedulePath(stringValue))
        } else {
            candidates.add(stringValue)
            candidates.add(uniqueId(stringValue))
        }
    }
    candidates.delete(undefined)
    candidates.delete("")
    return candidates
}

function normalizeSchedulePath(value) {
    const text = `${value}`
    if (!isSchedulePath(text)) return uniqueId(text)
    const parts = text.split("!")
    const scheduleId = uniqueId(parts.pop())
    return `${parts.join("!")}!${scheduleId}`
}

function isSchedulePath(value) {
    return `${value}`.includes("!")
}

function isPlanRoot(item) {
    return !!item?.[PlanRoot]
}
